import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate";
import { getFrontendVersion } from "../services/vmApi";

const SNOOZE_KEY = "update_banner_snoozed_until";
const SNOOZE_DURATION = 4 * 60 * 60 * 1000;

function compareVersions(v1, v2) {
  if (!v1 || !v2) return 0;

  const parts1 = v1
    .replace(/^v/, "")
    .split(".")
    .map(Number);
  const parts2 = v2
    .replace(/^v/, "")
    .split(".")
    .map(Number);

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;

    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }

  return 0;
}

export default function UpdateBanner() {
  const [showDialog, setShowDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const isSnoozed = () => {
    const snoozedUntil = localStorage.getItem(SNOOZE_KEY);
    if (!snoozedUntil) return false;

    const snoozedTime = parseInt(snoozedUntil, 10);
    const now = Date.now();

    return now < snoozedTime;
  };

  useEffect(() => {
    const checkVersion = async () => {
      if (isSnoozed()) {
        return;
      }

      try {
        const response = await getFrontendVersion();
        const serverVersion = response.data;
        const currentVersion = process.env.REACT_APP_VERSION;

        if (serverVersion && currentVersion) {
          const comparison = compareVersions(serverVersion, currentVersion);
          if (comparison > 0) {
            setShowDialog(true);
          }
        }
      } catch (error) {
        console.error("Erro ao verificar versão do frontend:", error);
      }
    };

    checkVersion();

    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleSnooze = () => {
    const snoozedUntil = Date.now() + SNOOZE_DURATION;
    localStorage.setItem(SNOOZE_KEY, snoozedUntil.toString());
    setShowDialog(false);
  };

  const handleUpdate = () => {
    setIsUpdating(true);

    localStorage.removeItem(SNOOZE_KEY);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }

    if ("caches" in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    setTimeout(() => {
      window.location.reload(true);
    }, 500);
  };

  return (
    <Dialog
      open={showDialog}
      onClose={handleSnooze}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SystemUpdateIcon sx={{ color: "#F69E3A", fontSize: 28 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Atualização Disponível
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Uma nova versão da aplicação está disponível. Atualize agora para
          obter as últimas funcionalidades, melhorias de desempenho e correções
          de bugs.
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1.5 }}>
          Se esta janela continuar aparecendo mesmo após clicar em "Atualizar
          Agora", pressione <strong>CTRL + R</strong> para atualizar
          manualmente.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleSnooze} color="inherit" variant="outlined">
          Lembrar Depois
        </Button>
        <Button
          onClick={handleUpdate}
          disabled={isUpdating}
          variant="contained"
          sx={{
            backgroundColor: "#F69E3A",
            color: "#000",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#E58D29",
            },
            "&:disabled": {
              backgroundColor: "#F69E3A",
              color: "#000",
              opacity: 0.7,
            },
          }}
        >
          {isUpdating ? "Atualizando..." : "Atualizar Agora"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
